import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["groupType", "menuHovered", "onMenuHoveredChange", "options", "value", "onChange", "popoverProps", "dropdownStyles"],
  _excluded2 = ["hovered"];
import { useRef, useCallback } from 'react';
import { AbstractDropdown } from '../../AbstractDropdown';
import { syntheticEvent } from '../../../utils/SyntheticEvent';
import { moveFocusToNext } from '../../../utils/dom/dom';
import { Controllable } from '../../Controllable';
import { HoverProvider } from '../../HoverProvider';
import { renderMenu } from '../utils/renderMultiLevelMenu';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
const RightCaret = /*#__PURE__*/_jsx(RightIcon, {
  size: "300",
  color: "icon-interactive-default"
});
const UncontrolledSubDropdownMenu = _ref => {
  let {
      groupType = 'none',
      menuHovered,
      onMenuHoveredChange,
      options,
      value,
      onChange,
      popoverProps,
      dropdownStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const menuRef = useRef(null);
  const hoverRef = useRef(false);
  const getComputedOpen = useCallback(() => {
    return rest.open || hoverRef.current || menuHovered || false;
  }, [rest.open, menuHovered]);
  const handleKeyDown = useCallback(evt => {
    const {
      key
    } = evt;
    if (key === 'ArrowRight' || key === 'Enter' || key === ' ') {
      if (!getComputedOpen() && key === 'Enter') {
        evt.preventDefault();
      }
      onMenuHoveredChange === null || onMenuHoveredChange === void 0 || onMenuHoveredChange(syntheticEvent(true));
      evt.stopPropagation();
    }
  }, [getComputedOpen, onMenuHoveredChange]);
  const handleKeyUp = useCallback(evt => {
    const {
      key
    } = evt;
    if (key === 'ArrowRight' || key === 'Enter' || key === ' ') {
      var _menuRef$current;
      const firstChild = (_menuRef$current = menuRef.current) === null || _menuRef$current === void 0 ? void 0 : _menuRef$current.querySelector('li');
      if ((firstChild === null || firstChild === void 0 ? void 0 : firstChild.firstElementChild) instanceof HTMLElement) {
        firstChild.firstElementChild.focus();
      }
      evt.preventDefault();
      evt.stopPropagation();
    }
  }, []);
  const handleKeyUpOnMenu = useCallback(evt => {
    const {
      key
    } = evt;
    if (key === 'Escape') {
      onMenuHoveredChange === null || onMenuHoveredChange === void 0 || onMenuHoveredChange(syntheticEvent(false));
      evt.stopPropagation();
    }
  }, [onMenuHoveredChange]);
  const handleKeyDownOnMenu = useCallback(evt => {
    const {
      key
    } = evt;
    if (!getComputedOpen()) return;
    if (key === 'ArrowLeft') {
      onMenuHoveredChange === null || onMenuHoveredChange === void 0 || onMenuHoveredChange(syntheticEvent(false));
      evt.stopPropagation();
      return;
    }
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      const menuElement = menuRef.current;
      if (menuElement) {
        if (moveFocusToNext('li', menuElement, key === 'ArrowUp')) {
          evt.preventDefault();
        }
      }
      evt.stopPropagation();
    }
  }, [getComputedOpen, onMenuHoveredChange]);
  const renderChildren = useCallback(() => {
    let updatedOptions = options;
    if (groupType === 'radio') {
      var _updatedOptions;
      updatedOptions = (_updatedOptions = updatedOptions) === null || _updatedOptions === void 0 ? void 0 : _updatedOptions.map(item => {
        const option = Object.assign({}, item);
        option.selected = value === item.value;
        option.onClick = evt => {
          var _item$onClick;
          evt.preventDefault();
          evt.stopPropagation();
          if (value !== option.value) {
            onChange === null || onChange === void 0 || onChange(syntheticEvent(option.value));
          }
          (_item$onClick = item.onClick) === null || _item$onClick === void 0 || _item$onClick.call(item, evt);
        };
        return option;
      });
    }
    return renderMenu(updatedOptions || []);
  }, [groupType, options, value, onChange]);
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, rest, {
    children: hoverProviderProps => {
      const {
          hovered
        } = hoverProviderProps,
        hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
      hoverRef.current = hovered || false;
      const computedOpen = getComputedOpen();
      return /*#__PURE__*/_jsx(AbstractDropdown, Object.assign({}, rest, hoverProviderRestProps, {
        dropdownStyles: dropdownStyles,
        open: computedOpen,
        popoverProps: Object.assign({}, popoverProps, {
          onMouseEnter: () => onMenuHoveredChange === null || onMenuHoveredChange === void 0 ? void 0 : onMenuHoveredChange(syntheticEvent(true)),
          onMouseLeave: () => onMenuHoveredChange === null || onMenuHoveredChange === void 0 ? void 0 : onMenuHoveredChange(syntheticEvent(false)),
          onKeyDown: handleKeyDownOnMenu,
          onKeyUp: handleKeyUpOnMenu
        }),
        caretRenderer: RightCaret,
        placement: "right bottom",
        onKeyUp: handleKeyUp,
        onKeyDown: handleKeyDown,
        animateOnToggle: false,
        autoPlacement: "horiz",
        menuWidth: "auto",
        children: /*#__PURE__*/_jsx("div", {
          "data-dropdown-menu": true,
          ref: menuRef,
          children: renderChildren()
        })
      }));
    }
  }));
};
export const SubDropdownMenu = Controllable(UncontrolledSubDropdownMenu, ['menuHovered', 'value']);